// Words and checks for Promotions, kept free of React so they can be tested:
// list labels, the buyer's voucher preview, and the dialog's form <-> API
// conversion and validation (mirrors admin-backend services/promotions.py).
import {
  DiscountKind,
  Distribution,
  Funding,
  GiveTrigger,
  PromoScope,
  PromoState,
  PromoType,
  Promotion,
  PromotionInput,
} from "../../common/requests/promotions/endpoints";

export const CLAIM_BASE_URL = "https://inflos.co/v/";
/** Inflos's standard fee, for the dialog's quick check */
export const STANDARD_FEE = 0.1;

// Admins are in Singapore; dates are entered and shown in SGT (UTC+8, no DST).
const SGT_OFFSET_MS = 8 * 3600 * 1000;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const money = (value: number | null | undefined, currency = "SGD"): string => {
  const n = Number(value ?? 0);
  const whole = Math.abs(n - Math.round(n)) < 0.005;
  const text = n.toLocaleString("en-US", { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 });
  return currency === "SGD" ? `S$${text}` : `${currency} ${text}`;
};

const sgtParts = (iso: string) => {
  const d = new Date(new Date(iso).getTime() + SGT_OFFSET_MS);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth(), d: d.getUTCDate(), h: d.getUTCHours(), min: d.getUTCMinutes() };
};

/** "15 Oct" (with the year when it isn't this year's) */
export const dayLabel = (iso: string, withYear = false): string => {
  const p = sgtParts(iso);
  return `${p.d} ${MONTHS[p.m]}${withYear ? ` ${p.y}` : ""}`;
};

/** The Dates column: "15–31 Oct", "11 Nov", "15 Oct – 3 Nov", "Feb 2025 – Mar 2026", "Always on". */
export function datesLabel(starts: string | null, expires: string | null, now = new Date()): string {
  const year = sgtParts(now.toISOString()).y;
  if (!starts && !expires) return "Always on";
  if (starts && !expires) return `From ${dayLabel(starts, sgtParts(starts).y !== year)}`;
  if (!starts && expires) return `Until ${dayLabel(expires, sgtParts(expires).y !== year)}`;
  const a = sgtParts(starts!);
  // An end at midnight means "through the day before".
  const endIso = new Date(new Date(expires!).getTime() - 1).toISOString();
  const b = sgtParts(endIso);
  if (a.y !== b.y) return `${MONTHS[a.m]} ${a.y} – ${MONTHS[b.m]} ${b.y}`;
  const suffix = a.y !== year ? ` ${a.y}` : "";
  if (a.m === b.m && a.d === b.d) return `${a.d} ${MONTHS[a.m]}${suffix}`;
  if (a.m === b.m) return `${a.d}–${b.d} ${MONTHS[a.m]}${suffix}`;
  return `${a.d} ${MONTHS[a.m]} – ${b.d} ${MONTHS[b.m]}${suffix}`;
}

type OfferFields = Pick<PromotionInput, "promo_type" | "discount_kind" | "amount" | "max_discount" | "min_spend">;

/** The Offer column: "S$10 off S$50+", "15% off up to S$20", "Up to S$8 off delivery". */
export function offerLabel(p: OfferFields): string {
  const min = p.min_spend > 0 ? ` on S$${p.min_spend}+` : "";
  if (p.promo_type === "free_delivery") return `Up to ${money(p.max_discount ?? p.amount)} off delivery${min}`;
  if (p.amount === null || p.amount === undefined) return "Not set";
  if (p.discount_kind === "percent") return `${p.amount}% off up to ${money(p.max_discount)}${min}`;
  return `${money(p.amount)} off${p.min_spend > 0 ? ` S$${p.min_spend}+` : ""}`;
}

export const TYPE_LABEL: Record<PromoType, string> = { voucher: "Voucher", free_delivery: "Free delivery" };

export function typeLabel(p: Pick<PromotionInput, "promo_type" | "first_order_only">): string {
  if (p.promo_type === "voucher" && p.first_order_only) return "New-buyer voucher";
  return TYPE_LABEL[p.promo_type];
}

export const TRIGGER_LABEL: Record<GiveTrigger, string> = {
  cart_reminder_2: "The 2nd cart reminder is sent",
  signup: "A buyer signs up",
  list: "I upload a list of buyers",
};

const TRIGGER_NOTE: Record<GiveTrigger, string> = {
  cart_reminder_2: "given by the 2nd cart reminder",
  signup: "given at sign-up",
  list: "given from a list",
};

export const FUNDING_LABEL: Record<Funding, string> = { inflos: "Inflos", sellers: "Sellers in the order", split: "Half each" };

export const ttlLabel = (hours: number): string =>
  hours % 24 === 0 && hours >= 72 ? `${hours / 24} days` : `${hours}h`;

/** The "How buyers get it" column. */
export function howLabel(p: Pick<PromotionInput, "distribution" | "max_claims" | "wallet_ttl_hours">): string {
  if (p.distribution === "code") return "Public code";
  if (p.distribution === "claim") return `Claim link${p.max_claims ? ` · first ${p.max_claims}` : ""}`;
  return `Given to buyers${p.wallet_ttl_hours ? ` · ${ttlLabel(p.wallet_ttl_hours)}` : ""}`;
}

/** The grey line under the name in the list. */
export function noteLabel(p: Promotion): string {
  if (p.legacy) return p.first_order_only ? "old Stripe code, first orders" : "old Stripe code";
  if (p.distribution === "code") return p.code ? `code ${p.code}` : "code not set";
  if (p.distribution === "claim") return p.claim_slug ? `/v/${p.claim_slug}` : "claim link not set";
  return p.give_trigger ? TRIGGER_NOTE[p.give_trigger] : "given to buyers";
}

export const usedLabel = (p: Promotion): string => `${p.stats.uses}${p.total_uses ? ` / ${p.total_uses}` : ""}`;

export const costLabel = (p: Promotion): string => `${money(p.stats.cost)}${p.budget ? ` / ${money(p.budget)}` : ""}`;

export const STATE_LABEL: Record<PromoState, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  live: "Live",
  paused: "Paused",
  ended: "Ended",
};

/** One line that sums a promotion up, under the detail dialog's title. */
export function summaryLine(p: Promotion): string {
  const how = p.distribution === "code" ? "public code" : p.distribution === "claim" ? "claim link" : "given to buyers";
  return [typeLabel(p), offerLabel(p), how, datesLabel(p.starts_at, p.expires_at), `paid by ${FUNDING_LABEL[p.funded_by].toLowerCase()}`].join(" · ");
}

// -- What buyers see

export interface VoucherPreviewText {
  /** Big text on the dark stub: "S$5", "10%", "FREE" */
  stub: string;
  /** Under it: "OFF" or "DELIVERY" */
  stubSmall: string;
  title: string;
  lines: string[];
  expiry: string;
}

type PreviewFields = OfferFields &
  Pick<PromotionInput, "title" | "scope" | "scope_values" | "first_order_only" | "distribution" | "wallet_ttl_hours" | "expires_at">;

/** The voucher card as My vouchers and the cart sheet show it. Same words
 * as the marketplace's WalletVoucher title/subtitle. */
export function buyerPreview(p: PreviewFields, now = new Date()): VoucherPreviewText {
  let stub: string;
  let stubSmall = "OFF";
  let title: string;
  if (p.promo_type === "free_delivery") {
    stub = "FREE";
    stubSmall = "DELIVERY";
    title = `Free delivery, up to ${money(p.max_discount ?? p.amount)}`;
  } else if (p.discount_kind === "percent") {
    stub = `${p.amount ?? 0}%`;
    title = `${p.amount ?? 0}% off your order${p.max_discount ? `, up to ${money(p.max_discount)}` : ""}`;
  } else {
    stub = money(p.amount ?? 0);
    title = `${money(p.amount ?? 0)} off your order`;
  }
  const lines = [p.min_spend > 0 ? `Min. spend ${money(p.min_spend)} on plants` : "No minimum spend"];
  const values = p.scope_values.filter(Boolean);
  if (p.scope === "sellers" && values.length) lines.push(`Only on plants from ${listWords(values)}`);
  if (p.scope === "genera" && values.length) lines.push(`Only on ${listWords(values)}`);
  if (p.first_order_only) lines.push("First order only");
  let expiry = "No end date";
  if (p.distribution !== "code" && p.wallet_ttl_hours) expiry = `Ends in ${ttlLabel(p.wallet_ttl_hours)}`;
  else if (p.expires_at) expiry = `Ends ${dayLabel(p.expires_at, sgtParts(p.expires_at).y !== sgtParts(now.toISOString()).y)}`;
  return { stub, stubSmall, title: p.title?.trim() || title, lines, expiry };
}

const listWords = (values: string[]) =>
  values.length <= 2 ? values.join(" and ") : `${values.slice(0, 2).join(", ")} and ${values.length - 2} more`;

/** A plain-English check on the size of the offer, next to the preview. */
export function quickCheck(p: OfferFields & Pick<PromotionInput, "funded_by">): string | null {
  if (p.promo_type === "free_delivery") {
    return p.max_discount ? `Costs up to ${money(p.max_discount)} per order that uses it.` : null;
  }
  if (!p.amount) return null;
  const share = p.discount_kind === "percent" ? p.amount / 100 : p.min_spend > 0 ? p.amount / p.min_spend : null;
  if (share === null) return `With no minimum spend, ${money(p.amount)} off can be most of a cheap order.`;
  const pct = Math.round(share * 100);
  const lead = p.discount_kind === "percent" ? `${p.amount}% off` : `At ${money(p.min_spend)}, ${money(p.amount)} is ${pct}% of the plants`;
  if (Math.abs(share - STANDARD_FEE) < 0.005) return `${lead}: the same as Inflos's whole fee on that order.`;
  if (share > STANDARD_FEE)
    return `${lead}: more than Inflos's ${STANDARD_FEE * 100}% fee${p.funded_by === "inflos" ? ", so Inflos loses money on that order" : ""}.`;
  return `${lead}: under Inflos's ${STANDARD_FEE * 100}% fee on that order.`;
}

// -- The dialog's form

/** Inputs as typed (strings), converted with toInput(). */
export interface PromoForm {
  name: string;
  title: string;
  promo_type: PromoType;
  discount_kind: DiscountKind;
  amount: string;
  max_discount: string;
  min_spend: string;
  scope: PromoScope;
  /** Comma separated */
  scope_values: string;
  first_order_only: boolean;
  distribution: Distribution;
  code: string;
  claim_slug: string;
  give_trigger: GiveTrigger;
  /** Hours as a string; "" = the promotion's end */
  wallet_ttl_hours: string;
  uses_per_buyer: string;
  /** "" = lifetime */
  per_buyer_window_days: string;
  total_uses: string;
  max_claims: string;
  budget: string;
  funded_by: Funding;
  /** datetime-local in SGT, "" = none */
  starts_at: string;
  expires_at: string;
}

export const emptyForm = (): PromoForm => ({
  name: "",
  title: "",
  promo_type: "voucher",
  discount_kind: "amount",
  amount: "",
  max_discount: "",
  min_spend: "0",
  scope: "all",
  scope_values: "",
  first_order_only: false,
  distribution: "code",
  code: "",
  claim_slug: "",
  give_trigger: "list",
  wallet_ttl_hours: "",
  uses_per_buyer: "1",
  per_buyer_window_days: "",
  total_uses: "",
  max_claims: "",
  budget: "",
  funded_by: "inflos",
  starts_at: "",
  expires_at: "",
});

const pad = (n: number) => String(n).padStart(2, "0");

/** ISO UTC -> "2026-10-15T00:00" in SGT, for datetime-local inputs */
export function toSgtInput(iso: string | null): string {
  if (!iso) return "";
  const p = sgtParts(iso);
  return `${p.y}-${pad(p.m + 1)}-${pad(p.d)}T${pad(p.h)}:${pad(p.min)}`;
}

/** "2026-10-15T00:00" (SGT) -> ISO UTC */
export function fromSgtInput(value: string): string | null {
  if (!value) return null;
  const t = Date.parse(`${value.length === 16 ? `${value}:00` : value}+08:00`);
  return Number.isNaN(t) ? null : new Date(t).toISOString();
}

const str = (v: number | null | undefined) => (v === null || v === undefined ? "" : String(v));

export function formFromPromotion(p: Promotion): PromoForm {
  return {
    name: p.name ?? "",
    title: p.title ?? "",
    promo_type: p.promo_type,
    discount_kind: p.discount_kind,
    amount: p.promo_type === "free_delivery" ? "" : str(p.amount),
    max_discount: str(p.max_discount),
    min_spend: str(p.min_spend),
    scope: p.scope,
    scope_values: (p.scope === "sellers" ? p.scope_labels : p.scope_values).join(", "),
    first_order_only: p.first_order_only,
    distribution: p.distribution,
    code: p.code ?? "",
    claim_slug: p.claim_slug ?? "",
    give_trigger: p.give_trigger ?? "list",
    wallet_ttl_hours: str(p.wallet_ttl_hours),
    uses_per_buyer: str(p.uses_per_buyer || 1),
    per_buyer_window_days: str(p.per_buyer_window_days),
    total_uses: str(p.total_uses),
    max_claims: str(p.max_claims),
    budget: str(p.budget),
    funded_by: p.funded_by,
    starts_at: toSgtInput(p.starts_at),
    expires_at: toSgtInput(p.expires_at),
  };
}

const num = (v: string): number | null => {
  const t = v.replace(/[^0-9.-]/g, "");
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

export const splitList = (v: string): string[] =>
  Array.from(new Set(v.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean)));

export function toInput(f: PromoForm): PromotionInput {
  const free = f.promo_type === "free_delivery";
  return {
    name: f.name.trim(),
    title: f.title.trim() || null,
    promo_type: f.promo_type,
    discount_kind: free ? "amount" : f.discount_kind,
    amount: free ? num(f.max_discount) : num(f.amount),
    max_discount: free || f.discount_kind === "percent" ? num(f.max_discount) : null,
    min_spend: num(f.min_spend) ?? 0,
    scope: f.scope,
    scope_values: f.scope === "all" ? [] : splitList(f.scope_values).map((v) => v.replace(/^@/, "")),
    first_order_only: f.first_order_only,
    distribution: f.distribution,
    code: f.distribution === "code" ? f.code.trim().toUpperCase() || null : null,
    claim_slug: f.distribution === "claim" ? f.claim_slug.trim().toLowerCase() || null : null,
    give_trigger: f.distribution === "given" ? f.give_trigger : null,
    wallet_ttl_hours: f.distribution === "code" ? null : num(f.wallet_ttl_hours),
    uses_per_buyer: num(f.uses_per_buyer) ?? 1,
    per_buyer_window_days: num(f.per_buyer_window_days),
    total_uses: num(f.total_uses),
    max_claims: f.distribution === "claim" ? num(f.max_claims) : null,
    budget: num(f.budget),
    funded_by: f.funded_by,
    starts_at: fromSgtInput(f.starts_at),
    expires_at: fromSgtInput(f.expires_at),
  };
}

export const STEPS = ["Type", "Offer", "Who gets it", "Limits & who pays", "Dates & review"] as const;

export type FormErrors = Partial<Record<keyof PromoForm, string>>;

/** Which step each field is on (0-based), to jump to the first problem. */
export const FIELD_STEP: Record<keyof PromoForm, number> = {
  promo_type: 0,
  name: 1,
  title: 1,
  discount_kind: 1,
  amount: 1,
  max_discount: 1,
  min_spend: 1,
  scope: 1,
  scope_values: 1,
  first_order_only: 1,
  distribution: 2,
  code: 2,
  claim_slug: 2,
  give_trigger: 2,
  wallet_ttl_hours: 2,
  max_claims: 2,
  uses_per_buyer: 3,
  per_buyer_window_days: 3,
  total_uses: 3,
  budget: 3,
  funded_by: 3,
  starts_at: 4,
  expires_at: 4,
};

const CODE_RE = /^[A-Z0-9]{3,20}$/;
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,62}[a-z0-9])$/;

/** Problems with one step (or every step when `step` is omitted). `publish`
 * adds what a promotion needs before going live (a code, a claim link, an
 * end date in the future). */
export function validate(f: PromoForm, opts: { step?: number; publish?: boolean; now?: Date } = {}): FormErrors {
  const e: FormErrors = {};
  const i = toInput(f);
  if (!i.name) e.name = "Give the promotion a name.";
  else if (i.name.length > 120) e.name = "Keep the name under 120 characters.";
  if (i.title && i.title.length > 80) e.title = "Keep the buyer title under 80 characters.";
  if (i.promo_type === "free_delivery") {
    if (i.max_discount === null || i.max_discount <= 0 || i.max_discount > 200) e.max_discount = "Set how much delivery it covers (up to S$200).";
  } else if (i.discount_kind === "percent") {
    if (i.amount === null || !Number.isInteger(i.amount) || i.amount < 1 || i.amount > 90) e.amount = "A % off is a whole number from 1 to 90.";
    if (i.max_discount === null || i.max_discount <= 0) e.max_discount = "Set the most it can take off.";
  } else if (i.amount === null || i.amount <= 0 || i.amount > 1000) {
    e.amount = "Set an amount off from S$0.01 to S$1,000.";
  } else if (i.min_spend > 0 && i.amount >= i.min_spend) {
    e.amount = "The amount off must be less than the minimum spend.";
  }
  if (num(f.min_spend) !== null && num(f.min_spend)! < 0) e.min_spend = "Minimum spend can't be negative.";
  if (i.scope !== "all" && !i.scope_values.length) e.scope_values = `Add at least one ${i.scope === "sellers" ? "seller" : "genus"}.`;
  if (i.distribution === "code" && i.code && !CODE_RE.test(i.code)) e.code = "3 to 20 letters or digits, no spaces.";
  if (i.distribution === "claim" && i.claim_slug && !SLUG_RE.test(i.claim_slug)) e.claim_slug = "3 to 64 lowercase letters, digits and hyphens.";
  if (i.max_claims !== null && i.max_claims < 1) e.max_claims = "At least 1, or leave empty.";
  if (i.uses_per_buyer < 1 || i.uses_per_buyer > 100 || !Number.isInteger(i.uses_per_buyer)) e.uses_per_buyer = "1 to 100.";
  if (i.per_buyer_window_days !== null && i.per_buyer_window_days < 1) e.per_buyer_window_days = "1 day or more.";
  if (i.total_uses !== null && i.total_uses < 1) e.total_uses = "At least 1, or leave empty.";
  if (i.budget !== null && i.budget <= 0) e.budget = "More than S$0, or leave empty.";
  if (f.starts_at && !i.starts_at) e.starts_at = "Not a date.";
  if (f.expires_at && !i.expires_at) e.expires_at = "Not a date.";
  if (i.starts_at && i.expires_at && i.expires_at <= i.starts_at) e.expires_at = "The end must be after the start.";
  if (opts.publish) {
    if (i.distribution === "code" && !i.code) e.code = e.code ?? "Set the code buyers type.";
    if (i.distribution === "claim" && !i.claim_slug) e.claim_slug = e.claim_slug ?? "Set the claim link.";
    if (i.expires_at && new Date(i.expires_at) <= (opts.now ?? new Date())) e.expires_at = e.expires_at ?? "The end has passed.";
  }
  if (opts.step === undefined) return e;
  return Object.fromEntries(Object.entries(e).filter(([k]) => FIELD_STEP[k as keyof PromoForm] === opts.step)) as FormErrors;
}

/** Fields that can still change once a promotion has been published (as the backend). */
export const SAFE_FIELDS: (keyof PromoForm)[] = [
  "name",
  "title",
  "starts_at",
  "expires_at",
  "uses_per_buyer",
  "per_buyer_window_days",
  "total_uses",
  "max_claims",
  "budget",
  "wallet_ttl_hours",
];

/** "2026-10" -> "Oct" (or "Oct 25" for January and the first bar) */
export function monthLabel(month: string, first: boolean): string {
  const [y, m] = month.split("-").map(Number);
  const name = MONTHS[m - 1];
  return first || m === 1 ? `${name} ${String(y).slice(2)}` : name;
}
